import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { format } from "date-fns";
import { useAuth } from "@/lib/authContext";
import { api, Account, Invoice } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { Plus, FileText, Trash2, Edit2, Eye, Search, Loader2, Clock, DollarSign, RotateCcw } from "lucide-react";

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 border-gray-300",
  sent: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-300",
  void: "bg-red-100 text-red-800 border-red-300",
};

export default function Invoices() {
  const { user } = useAuth();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [, navigate] = useLocation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [creating, setCreating] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];
  const [newInvoice, setNewInvoice] = useState({
    account_id: "",
    invoice_number: "",
    invoice_date: todayStr,
    due_date: todayStr,
    notes: "",
  });
  const [showFromTime, setShowFromTime] = useState(false);
  const [fromTimeForm, setFromTimeForm] = useState({
    account_id: "",
    date_from: "",
    date_to: new Date().toISOString().split("T")[0],
    hourly_rate: "150",
    tax_rate: "0",
  });
  const [creatingFromTime, setCreatingFromTime] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [invoicesData, accountsData] = await Promise.all([
        api.getInvoices({ status: statusFilter }),
        api.getAccounts(),
      ]);
      setInvoices(invoicesData);
      setAccounts(accountsData);
    } catch (error: any) {
      toast({ title: "Error loading data", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    if (user && ["manager", "admin"].includes(user.role)) {
      loadData();
    }
  }, [user, loadData]);

  const filteredInvoices = invoices.filter((inv) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(search) ||
      inv.accounts?.account_name.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount: string | number | null | undefined) => {
    if (amount === null || amount === undefined) return "$0.00";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(isNaN(num) ? 0 : num);
  };

  const handleCreateInvoice = async () => {
    if (!newInvoice.account_id) {
      toast({ title: "Error", description: "Please select a client", variant: "destructive" });
      return;
    }
    
    setCreating(true);
    try {
      const created = await api.createInvoice({
        account_id: newInvoice.account_id,
        invoice_number: newInvoice.invoice_number || undefined,
        invoice_date: newInvoice.invoice_date,
        due_date: newInvoice.due_date || undefined,
        notes: newInvoice.notes || undefined,
      });
      toast({ title: "Invoice created" });
      setShowNewInvoice(false);
      const resetToday = new Date().toISOString().split("T")[0];
      setNewInvoice({
        account_id: "",
        invoice_number: "",
        invoice_date: resetToday,
        due_date: resetToday,
        notes: "",
      });
      navigate(`/invoices/${created.id}/edit`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFromTimeEntries = async () => {
    if (!fromTimeForm.account_id || !fromTimeForm.date_from || !fromTimeForm.date_to) {
      toast({ title: "Error", description: "Client, start date, and end date are required", variant: "destructive" });
      return;
    }
    setCreatingFromTime(true);
    try {
      const created = await api.createInvoiceFromTimeEntries({
        account_id: fromTimeForm.account_id,
        date_from: fromTimeForm.date_from,
        date_to: fromTimeForm.date_to,
        hourly_rate: parseFloat(fromTimeForm.hourly_rate) || 150,
        tax_rate: parseFloat(fromTimeForm.tax_rate) || 0,
      });
      toast({ title: "Invoice created from time entries" });
      setShowFromTime(false);
      setFromTimeForm({ account_id: "", date_from: "", date_to: new Date().toISOString().split("T")[0], hourly_rate: "150", tax_rate: "0" });
      navigate(`/invoices/${created.id}`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setCreatingFromTime(false);
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    const target = invoices.find((inv) => inv.id === id);
    if (!(await askConfirm({
      title: "Delete this invoice?",
      subject: "invoice",
      name: target?.invoice_number ? `Invoice ${target.invoice_number}` : null,
      body: "will be deleted.",
      confirmLabel: "Delete",
      destructive: true,
    }))) return;

    try {
      await api.deleteInvoice(id);
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
      toast({ title: "Invoice deleted" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleQuickMarkPaid = async (id: string) => {
    const paidDate = new Date().toISOString().split("T")[0];
    if (!(await askConfirm({
      title: "Mark this invoice as paid?",
      subject: "invoice",
      body: `will be marked paid, dated ${paidDate}.`,
      confirmLabel: "Mark paid",
    }))) return;
    try {
      const updated = await api.updateInvoice(id, { status: "paid", paid_date: paidDate });
      setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, ...updated } : inv));
      toast({ title: "Invoice marked as paid" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleRevertToDraft = async (id: string) => {
    if (!(await askConfirm({
      title: "Revert this invoice to Draft?",
      subject: "invoice",
      body: "will return to Draft.",
      lines: ["This is an admin action."],
      confirmLabel: "Revert to draft",
    }))) return;
    try {
      const updated = await api.updateInvoice(id, { status: "draft" });
      setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, ...updated } : inv));
      toast({ title: "Invoice reverted to draft" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  if (!user || !["manager", "admin"].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Access denied. Manager or admin role required.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="text-page-title">Invoices</h1>
          <p className="text-muted-foreground">Manage invoices and billing</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowFromTime(true)} data-testid="button-from-time-entries">
            <Clock className="w-4 h-4 mr-2" />
            From Time Entries
          </Button>
          <Button onClick={() => setShowNewInvoice(true)} data-testid="button-new-invoice">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="input-search-invoices"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setLoading(true); }}>
              <SelectTrigger className="w-40" data-testid="select-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No invoices found</h3>
              <p className="text-muted-foreground mb-4">
                {statusFilter !== "all" 
                  ? "Try changing your filter or search terms" 
                  : "Get started by creating your first invoice"}
              </p>
              {statusFilter === "all" && (
                <Button onClick={() => setShowNewInvoice(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`row-invoice-${invoice.id}`}>
                    <TableCell className="font-medium">
                      {invoice.invoice_number || "-"}
                    </TableCell>
                    <TableCell>{invoice.accounts?.account_name || "-"}</TableCell>
                    <TableCell>
                      {format(new Date(invoice.invoice_date), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      {invoice.due_date 
                        ? format(new Date(invoice.due_date), "MMM d, yyyy") 
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={statusColors[invoice.status] || statusColors.draft}
                      >
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(invoice.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/invoices/${invoice.id}`}>
                          <Button variant="ghost" size="icon" title="View / edit">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {invoice.status === "sent" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Mark as paid"
                            onClick={() => handleQuickMarkPaid(invoice.id)}
                          >
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        {invoice.status === "sent" && user.role === "admin" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Revert to draft (admin)"
                            onClick={() => handleRevertToDraft(invoice.id)}
                          >
                            <RotateCcw className="w-4 h-4 text-amber-500" />
                          </Button>
                        )}
                        {(invoice.status === "draft" || user.role === "admin") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Delete"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showNewInvoice} onOpenChange={setShowNewInvoice}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
            <DialogDescription>
              Create a new invoice draft. You can add line items after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="account">Client *</Label>
              <SearchableSelect
                value={newInvoice.account_id}
                onValueChange={(v) => setNewInvoice({ ...newInvoice, account_id: v })}
                options={accounts.map((a) => ({ value: a.id, label: a.account_name }))}
                placeholder="Select a client"
                searchPlaceholder="Search clients..."
                emptyText="No clients found."
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_number">Invoice Number</Label>
              <Input
                id="invoice_number"
                value={newInvoice.invoice_number}
                onChange={(e) => setNewInvoice({ ...newInvoice, invoice_number: e.target.value })}
                placeholder="Optional - auto-generated if blank"
                data-testid="input-new-invoice-number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoice_date">Invoice Date</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={newInvoice.invoice_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })}
                  data-testid="input-new-invoice-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                  data-testid="input-new-invoice-due-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newInvoice.notes}
                onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                placeholder="Optional notes..."
                data-testid="input-new-invoice-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewInvoice(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateInvoice}
              disabled={creating}
              data-testid="button-create-invoice-submit"
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFromTime} onOpenChange={setShowFromTime}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Invoice from Time Entries</DialogTitle>
            <DialogDescription>
              Pulls all billable time entries for a client within the date range and creates a draft invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <SearchableSelect
                value={fromTimeForm.account_id}
                onValueChange={(v) => setFromTimeForm({ ...fromTimeForm, account_id: v })}
                options={accounts.map((a) => ({ value: a.id, label: a.account_name }))}
                placeholder="Select a client"
                searchPlaceholder="Search clients..."
                emptyText="No clients found."
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Date *</Label>
                <Input
                  type="date"
                  value={fromTimeForm.date_from}
                  onChange={(e) => setFromTimeForm({ ...fromTimeForm, date_from: e.target.value })}
                  data-testid="input-from-time-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label>To Date *</Label>
                <Input
                  type="date"
                  value={fromTimeForm.date_to}
                  onChange={(e) => setFromTimeForm({ ...fromTimeForm, date_to: e.target.value })}
                  data-testid="input-from-time-date-to"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={fromTimeForm.hourly_rate}
                  onChange={(e) => setFromTimeForm({ ...fromTimeForm, hourly_rate: e.target.value })}
                  data-testid="input-from-time-hourly-rate"
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={fromTimeForm.tax_rate}
                  onChange={(e) => setFromTimeForm({ ...fromTimeForm, tax_rate: e.target.value })}
                  data-testid="input-from-time-tax-rate"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFromTime(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFromTimeEntries}
              disabled={creatingFromTime}
              data-testid="button-from-time-submit"
            >
              {creatingFromTime ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
